import React, { useEffect, useRef } from "react";

// Minimal YouTube IFrame Player API typings.
declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
    __ytApiLoading?: boolean;
    __ytApiReadyCbs?: Array<() => void>;
  }
}

// Load the YT IFrame API once and run cb when ready.
function whenYTReady(cb: () => void) {
  if (window.YT && window.YT.Player) {
    cb();
    return;
  }
  window.__ytApiReadyCbs = window.__ytApiReadyCbs || [];
  window.__ytApiReadyCbs.push(cb);

  if (!window.__ytApiLoading) {
    window.__ytApiLoading = true;
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev && prev();
      (window.__ytApiReadyCbs || []).forEach((fn) => fn());
      window.__ytApiReadyCbs = [];
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
  }
}

interface Props {
  videoId: string;
  title: string;
  onEnded: () => void;
}

/**
 * YouTube player that reports when the video finishes (state ENDED = 0),
 * so the lesson can be auto-completed and advanced.
 */
const YouTubePlayer: React.FC<Props> = ({ videoId, title, onEnded }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  // Keep latest onEnded without re-creating the player.
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  useEffect(() => {
    let destroyed = false;

    whenYTReady(() => {
      if (destroyed || !containerRef.current) return;
      // Clean any previous instance.
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
      }
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        playerVars: {
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
        },
        events: {
          onStateChange: (e: any) => {
            // 0 = ENDED
            if (e.data === 0) onEndedRef.current();
          },
        },
      });
    });

    return () => {
      destroyed = true;
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy();
        } catch {
          /* ignore */
        }
        playerRef.current = null;
      }
    };
  }, [videoId]);

  return (
    <div className="absolute inset-0 w-full h-full">
      <div ref={containerRef} className="w-full h-full" title={title} />
    </div>
  );
};

export default YouTubePlayer;
